import { useEffect, type ReactNode } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Logo } from '../components/Logo'

type LegalSection = {
  title: string
  content: ReactNode
}

type LegalPageProps = {
  eyebrow: string
  title: string
  summary: string
  sections: LegalSection[]
}

const effectiveDate = 'July 22, 2026'

function LegalPage({ eyebrow, title, summary, sections }: LegalPageProps) {
  useEffect(() => {
    document.title = `${title} | BlackRock Developers`
    window.scrollTo(0, 0)
    return () => {
      document.title = 'BlackRock Developers | Custom & Spec Homes in Western North Carolina'
    }
  }, [title])

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="page-width legal-header__inner">
          <Logo />
          <Link to="/" className="legal-header__back"><ArrowLeft size={16} /> Back to website</Link>
        </div>
      </header>
      <main className="legal-main">
        <article className="page-width legal-article" aria-labelledby="legal-title">
          <div className="legal-article__intro">
            <p className="eyebrow"><span />{eyebrow}</p>
            <h1 id="legal-title">{title}</h1>
            <p>{summary}</p>
            <span>Effective {effectiveDate}</span>
          </div>
          <div className="legal-article__body">
            {sections.map((section) => (
              <section key={section.title}>
                <h2>{section.title}</h2>
                {section.content}
              </section>
            ))}
          </div>
        </article>
      </main>
      <footer className="legal-footer">
        <div className="page-width">
          <p>© {new Date().getFullYear()} BlackRock Developers. All rights reserved.</p>
          <nav aria-label="Legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}

export function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Your information"
      title="Privacy Policy"
      summary="This policy explains what information BlackRock Developers collects through this website, why we use it, and the choices available to you."
      sections={[
        {
          title: '1. Information we collect',
          content: <>
            <p>When you submit an inquiry, we collect the information you choose to provide, such as your name, email address, phone number, project type, location or timing details, and message. We also record the submission time and the version of the privacy and terms notice presented with the form.</p>
            <p>Our hosting and security providers may automatically process limited technical information such as IP address, browser and device type, request timestamps, and security logs. The public website does not currently use advertising trackers or sell personal information.</p>
            <p>Authorized site administrators may use a phone number to receive a one-time security code. Firebase Authentication sends and stores that number for authentication, fraud prevention, and abuse prevention.</p>
          </>,
        },
        {
          title: '2. How we use information',
          content: <>
            <p>We use information to respond to inquiries, evaluate potential projects, communicate about requested services, operate and secure the website, maintain business records, prevent misuse, and comply with legal obligations.</p>
            <p>Submitting an inquiry does not create a construction, brokerage, design, fiduciary, or other professional relationship.</p>
          </>,
        },
        {
          title: '3. When information is shared',
          content: <>
            <p>We may share information with service providers that host, secure, or support the website, including Google Firebase. These providers process information on our behalf under their own contractual and security obligations.</p>
            <p>We may also disclose information when required by law, to protect rights or safety, to investigate misuse, or as part of a merger, financing, sale, or transfer of the business. We do not sell or rent inquiry information.</p>
          </>,
        },
        {
          title: '4. Retention and security',
          content: <>
            <p>We retain inquiry information only as long as reasonably needed for the purposes described above, legitimate business records, dispute resolution, and legal compliance. Retention periods may vary depending on whether a project relationship develops.</p>
            <p>We use access controls, encrypted connections, restricted administrator permissions, and managed cloud services to protect information. No internet service can guarantee absolute security.</p>
          </>,
        },
        {
          title: '5. Your choices',
          content: <>
            <p>You may ask to access, correct, or delete information you submitted, or ask us to stop nonessential communications, subject to applicable law and legitimate recordkeeping needs. Use the website inquiry form and begin your message with “Privacy request” so it can be routed appropriately.</p>
            <p>Browser controls can limit local storage or cookies, although essential authentication and security features may not work correctly when those technologies are blocked.</p>
          </>,
        },
        {
          title: '6. Children’s privacy',
          content: <p>This website is intended for adults planning or researching residential construction. We do not knowingly collect personal information from children under 13. If you believe a child submitted information, contact us so we can review and remove it.</p>,
        },
        {
          title: '7. Changes and contact',
          content: <p>We may update this policy as our services, providers, or legal obligations change. The effective date above identifies the current version. Questions or privacy requests may be submitted through the contact form on the <Link to="/#inquiry-form">main website</Link>.</p>,
        },
      ]}
    />
  )
}

export function TermsOfService() {
  return (
    <LegalPage
      eyebrow="Website terms"
      title="Terms of Service"
      summary="These terms govern use of the BlackRock Developers website. By using the site or submitting an inquiry, you agree to these terms."
      sections={[
        {
          title: '1. Informational purpose',
          content: <>
            <p>This website presents general information about BlackRock Developers, examples of residential work, and ways to start a conversation. Website content is not a bid, guarantee, professional opinion, or offer to enter into a construction or real-estate transaction.</p>
            <p>A binding relationship exists only after the appropriate parties sign a separate written agreement. Project scope, schedule, pricing, materials, availability, and site conditions are always subject to independent review and written confirmation.</p>
          </>,
        },
        {
          title: '2. Portfolio and property information',
          content: <p>Photographs, renderings, descriptions, dimensions, features, addresses, completion dates, and other project details are illustrative and may be edited, approximate, supplied by third parties, or relate to work completed with other participants. They do not promise that identical results, materials, views, pricing, or availability can be provided.</p>,
        },
        {
          title: '3. Acceptable use',
          content: <p>You may use the site for lawful personal and business evaluation. You may not interfere with the site, attempt unauthorized access, submit malicious code or false information, scrape the site at disruptive volume, impersonate another person, or use site content in a misleading or unlawful way.</p>,
        },
        {
          title: '4. Intellectual property',
          content: <p>The site design, branding, text, graphics, and original media are owned by BlackRock Developers or used with permission and are protected by applicable intellectual-property laws. You may not reproduce, publish, distribute, or create commercial derivative works without written permission. Third-party names, listing information, and media remain the property of their respective owners.</p>,
        },
        {
          title: '5. Information you submit',
          content: <p>You represent that information you submit is accurate, lawful, and yours to provide. You authorize us to use it to review and respond to your request, operate the site, maintain appropriate records, and protect against misuse as described in the <Link to="/privacy">Privacy Policy</Link>.</p>,
        },
        {
          title: '6. Third-party services and links',
          content: <p>The site may link to Instagram, property listings, mapping, cloud, or other third-party services. We do not control those services and are not responsible for their availability, accuracy, security, or privacy practices. Your use of them is governed by their own terms.</p>,
        },
        {
          title: '7. Disclaimers and limitation of liability',
          content: <>
            <p>The site is provided on an “as available” basis. To the extent permitted by law, we disclaim implied warranties relating to the site, including merchantability, fitness for a particular purpose, and noninfringement.</p>
            <p>To the extent permitted by law, BlackRock Developers and its owners, personnel, and service providers will not be liable for indirect, incidental, special, consequential, or punitive damages arising from website use. Nothing in these terms limits liability that cannot lawfully be limited.</p>
          </>,
        },
        {
          title: '8. Governing law and changes',
          content: <p>These terms are governed by the laws of North Carolina, without regard to conflict-of-law rules. Any dispute relating solely to the website will be brought in a court of competent jurisdiction in North Carolina. We may revise these terms, and the effective date above identifies the current version.</p>,
        },
        {
          title: '9. Contact',
          content: <p>Questions about these terms may be submitted through the contact form on the <Link to="/#inquiry-form">main website</Link>.</p>,
        },
      ]}
    />
  )
}
